<script setup lang="ts">
import { watchDebounced, useDebounceFn, useScroll, useElementHover } from '@vueuse/core'

const props = defineProps({
  items: {
    type: Array<string>,
    required: true,
  },
  modelValue: {
    type: String,
    required: true,
  },
  gaInteraction: {
    type: String,
    default: 'menu_click',
  },
  gaEvent: {
    type: Boolean,
    default: true,
  },
})
const emits = defineEmits(['update:modelValue', 'scrollStateChange'])
const $filterWrapper = ref()
const $filterList = ref()
const { width: widthWrapper } = useElementSize($filterWrapper)
const { width: widthList } = useElementSize($filterList)
const { smAndDown: tabletAndMobile } = useDisplay()
const { arrivedState, isScrolling } = useScroll($filterList)

watch([arrivedState, isScrolling], () => emits('scrollStateChange', { isScrolling, arrivedState }), { deep: true })

const moreBtnList = ref<string[]>([])

watchDebounced(
  widthWrapper,
  () => {
    updateMore()
  },
  {
    debounce: 50,
    maxWait: 500,
  }
)

watchDebounced(
  widthList,
  () => {
    updateMore()
  },
  { debounce: 50 }
)

let cacheButtonSizes = [] as { name: string; width: number }[]

watch(
  () => props.items,
  () => {
    resetMore()
  }
)

function resetMore() {
  moreBtnList.value = [] // reset to get all actual sizes
  cacheButtonSizes = []
  updateMore()
}

const updateMore = useDebounceFn(() => {
  if (tabletAndMobile.value) {
    moreBtnList.value = []
    return
  }
  const wrapperEle = $filterWrapper.value as HTMLElement
  let availableWidth = wrapperEle.clientWidth
  const filterListEle = $filterList.value as HTMLElement
  for (const one of wrapperEle.children) {
    if (one.className !== 'fliterbox__list') availableWidth -= one.clientWidth
  }

  if (availableWidth >= filterListEle.clientWidth && moreBtnList.value.length == 0) return // no cut off

  // caching
  if (moreBtnList.value.length == 0 && cacheButtonSizes.length != filterListEle.children.length) {
    cacheButtonSizes = []
    for (const one of filterListEle.children) {
      cacheButtonSizes.push({ name: one.innerHTML, width: one.clientWidth })
    }
  } else if (cacheButtonSizes[0].width != filterListEle.children[0].clientWidth) {
    // console.log('first size: ', filterListEle.children[0].clientWidth)
    resetMore()
    return
  }
  ///

  if (cacheButtonSizes.reduce((prev, cur) => prev + cur.width, 0) <= availableWidth) {
    moreBtnList.value = []
    return
  }

  let totalWidth = 120 // aprox. more button size
  const _items = [...props.items]
  const moreArr = []
  for (const one of cacheButtonSizes) {
    totalWidth += one.width
    if (availableWidth < totalWidth) {
      moreArr.push(one.name)
    }
  }
  moreBtnList.value = moreArr
  // console.log(moreArr, totalWidth, availableWidth)
}, 200)

onMounted(() => {
  // For Mobile Tablet
  $filterList.value.scrollTo(1000, 0)
  setTimeout(() => {
    $filterList.value.scrollTo({
      left: 0,
      behavior: 'smooth',
    })
  }, 100)
})

const menuRef = ref()
const isHovered = useElementHover(menuRef, { delayEnter: 50 })
</script>
<template>
  <div class="filterbox-shadow"></div>
  <div
    class="filterbox font2 md"
    data-section-number="1"
    data-section-name="Contents Header"
    v-ga-section-view
    v-bind="$attrs"
  >
    <div class="filterbox__wrapper" ref="$filterWrapper">
      <slot name="prepend" />
      <div class="fliterbox__list" ref="$filterList">
        <div
          class="filterbox__item"
          v-for="(item, index) in items.filter((o) => !moreBtnList.includes(o))"
          :key="`item-${index}`"
          :class="item === modelValue ? 'active' : ''"
          @click="emits('update:modelValue', item)"
          v-ga-event="
            gaEvent == false
              ? null
              : {
                  interaction: gaInteraction,
                  sectionContent: item,
                }
          "
        >
          <slot name="item" :item="item">{{ item }}</slot>
        </div>
        <div v-if="moreBtnList.length > 0" class="filterbox__item more-btn" ref="menuRef">
          MORE <i-local-nav-arrow />
          <Transition name="bounce-dropdown">
            <div v-show="isHovered" class="sub-menu-container">
              <div class="sub-menu-box">
                <div
                  class="filterbox__item"
                  v-for="(item, index) in moreBtnList"
                  :key="`item--${index}`"
                  :class="item === modelValue ? 'active' : ''"
                  @click="emits('update:modelValue', item)"
                >
                  <slot name="item" :item="item">{{ item }}</slot>
                </div>
              </div>
            </div>
          </Transition>
        </div>
      </div>
      <slot name="append" />
    </div>
  </div>
</template>
<style lang="scss" scoped>
$bgColor: rgba(55, 40, 30, 0.9); //rgba(84, 52, 23, 0.9);
.filterbox-shadow {
  // position: absolute;
  scroll-margin-top: 88px;
  order: -1;
}
.filterbox {
  position: sticky;
  top: 88px;
  width: 100%;
  z-index: 10;
  order: -1;
  margin-bottom: 10px;
}
.filterbox__wrapper {
  display: flex;
  align-items: center;
  background-color: $bgColor;
  backdrop-filter: blur(10px);
  border-radius: 10px;
  color: white;
}

.fliterbox__list {
  display: inline-flex;
  justify-content: space-between;
  &::before,
  &::after {
    content: '';
  }
  align-items: center;
  width: 100%;
  padding: 10px;
}
.filterbox__item {
  cursor: pointer;
  font-weight: bold;
  text-transform: uppercase;
  padding: 4px 20px;
  border-radius: 20px;
  transition: background 0.3s;
  user-select: none;
  white-space: nowrap;
  display: inline-flex;
  align-items: center;
  column-gap: 0.5em;
  &.active {
    color: #4d4136;
    background: var(--accent-color2);
    border-radius: 20px;
  }
}

// exclude touchscreen
@media (hover: hover) {
  .filterbox__item {
    &:hover {
      color: #4d4136;
      background: var(--accent-color2);
      border-radius: 20px;
    }
  }
}

.more-btn {
  display: inline-flex;
  align-items: center;
  column-gap: 6px;
  position: relative;
  &:hover {
    background-color: rgba(0, 0, 0, 0.2);
    color: white;
  }
}

.sub-menu-container {
  position: absolute;
  right: 0;
  top: 100%;
  padding-top: 20px;
  cursor: initial;
  // border: 1px solid red;
  .sub-menu-box {
    background-color: $bgColor;
    border-radius: 10px;
    padding: 8px;
    width: 100%;
    display: flex;
    flex-direction: column;
    row-gap: 8px;
  }
  // &:not(:first-child):hover ul {
  //   left: -1px;
  // }
}

@media #{$smallDesktop} {
  .filterbox {
    font-size: 16px;
  }
}
@media #{$tablet} {
  .filterbox {
    font-size: 14px;
  }
  .fliterbox__list {
    overflow-x: auto;
    &::-webkit-scrollbar {
      display: none;
    }
    -webkit-mask-image: linear-gradient(90deg, transparent, black 14px, black calc(100% - 30px), transparent 100%);
    margin: 0 2px;
  }
}
@media #{$tablet} {
  .filterbox__item {
    padding: 4px 12px;
  }
}
@media #{$mobile} {
  .filterbox-shadow {
    scroll-margin-top: 55px;
  }
  .filterbox {
    top: 56px;
    width: auto;
    margin: 0 -20px; // ContentWrapper's padding
  }
  .filterbox__wrapper {
    border-radius: 0px;
    // background-color: rgba(84, 52, 23, 0.9);
  }
  .filterbox__item {
    padding: 4px 15px;
  }
}
</style>
